import React, { useEffect, useState } from 'react';
import { ImageOff, Palette } from 'lucide-react';
import { useMediaBlob } from '../hooks/useMediaBlobUrl';
import SlideVisualStage from './SlideVisualStage';
import type { PresentationTemplate, SlideVisual } from '../types';

/**
 * Voorbeeldafbeelding in een 16:9-kader. De afbeelding wordt met `object-contain` getoond: nooit
 * afgesneden, de oorspronkelijke beeldverhouding blijft behouden en een afwijkend formaat (4:3, staand)
 * krijgt netjes randen binnen het kader. Ontbrekende of onleesbare afbeeldingen geven een duidelijke melding.
 */
export function MediaThumbnail({ mediaId, alt, className = '' }: { mediaId: string | null | undefined; alt: string; className?: string }) {
  const { url, failed } = useMediaBlob(mediaId);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const unavailable = failed || broken || !mediaId;
  return (
    <div className={`flex aspect-video items-center justify-center overflow-hidden bg-slate-100 ${className}`}>
      {url && !broken ? (
        <img src={url} alt={alt} onError={() => setBroken(true)} className="h-full w-full object-contain" />
      ) : unavailable ? (
        <span role="img" aria-label={`${alt}: afbeelding niet beschikbaar`} className="flex flex-col items-center gap-1 px-2 text-center text-xs text-slate-500">
          <ImageOff className="h-5 w-5" />Afbeelding niet beschikbaar
        </span>
      ) : null}
    </div>
  );
}

/** Voorbeeld van een bewerkbare slide (zelfde weergave als op de beamer), in een 16:9-kader. */
export function VisualSlideThumbnail({ visual, className = '' }: { visual: SlideVisual | null | undefined; className?: string }) {
  if (!visual || (!(visual.elements || []).length && !visual.background)) {
    return (
      <div className={`flex aspect-video items-center justify-center bg-slate-100 text-xs text-slate-500 ${className}`}>
        Geen voorbeeld
      </div>
    );
  }
  return (
    <div className={`aspect-video overflow-hidden bg-slate-100 ${className}`}>
      <SlideVisualStage visual={visual} previewMode />
    </div>
  );
}

export default function TemplatePreview({ template }: { template: PresentationTemplate }) {
  if (template.template_type === 'editable') return <VisualSlideThumbnail visual={template.preview_slide} />;
  if (template.template_type === 'slides') {
    return <MediaThumbnail mediaId={template.preview_media_id} alt={`Voorbeeld van ${template.name}`} />;
  }
  return (
    <div
      className="flex aspect-video items-center justify-center"
      style={{ backgroundColor: template.primary_color || '#f1f5f9', color: template.secondary_color || '#475569' }}
      aria-hidden="true"
    >
      <Palette className="h-8 w-8 opacity-60" />
    </div>
  );
}
